import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Paper,
    Typography,
    TextField,
    Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth, useNavigation, useLoading, useSnackbar } from "../../../Context/Contexts";
import GroupIcon from "@mui/icons-material/Group";
import BlockIcon from "@mui/icons-material/Block";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { ListQuery } from "../../../Types/CommonTypes";
import { UserViewModel } from "../../../Types/ExportTypes";
import CenteredListCard from "../../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../../components/List/ListCardHeader";
import useCachedListQuery from "../../../hooks/useCachedListQuery";
import { useTranslation } from "react-i18next";
import { buildQueryFilter } from "../../../Utils/QueryFilterUtils";
import ResponsivePagination from "../../../components/ResponsivePagination";
const UserList: React.FC = () => {
    const [viewModel, setViewModel] = useState<UserViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [query, setQuery] = useCachedListQuery({
        page: 0,
        pageSize: 10,
        filter: "",
        filters: [],
    });
    const [filter, setFilter] = useState<string>("");
    const [filterProperty, setFilterProperty] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const { authService } = useAuth();
    const { navigate } = useNavigation();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const fetchItems = async () => {
        setLoading(true);
        try {
            const response = await authService.post<{ items: UserViewModel[]; count: number }>(
                "/userApi/GetUserList",
                query
            );
            setViewModel(response.items);
            setTotalCount(response.count);
        } catch (error : any) {
            showSnackbar(`${t("common.failedToFetchData")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, [query]);

    useEffect(() => {
        if (loading) showLoading();
        else hideLoading();
    }, [loading, showLoading, hideLoading]);

    const isBooleanProperty = filterProperty === "bo_deleted";

    useEffect(() => {
        if (isBooleanProperty) setFilter("false");
    }, [isBooleanProperty]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        var localFilterProperty = filterProperty;
        //if (!filter) return;
        if (!localFilterProperty?.trim() && filter?.trim()) {
            localFilterProperty = "Default";
        }

        if (localFilterProperty?.trim() && (filter?.trim() || isBooleanProperty)) {
            const newFilter = buildQueryFilter(localFilterProperty, filter);
            const isDuplicate = query.filters?.some(
                (f) => f.property === newFilter.property && f.value === newFilter.value
            );
            if (isDuplicate) return;

            const newFilters = [...(query.filters ?? []), newFilter];
            setQuery({ ...query, filters: newFilters, page: 0, filter: "" });
        } else {
            setQuery({ ...query, filter: filter, page: 0 });
        }

        setFilter("");
        setFilterProperty("");
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handleSelect = (id?: number | null) => {
        navigate({ path: "/admin/user/edit", params: { id } });
    };

    const handleCreate = () => {
        navigate({ path: "/admin/user/edit", params: { } });
    };

    return (
        <CenteredListCard title={t("common.users")}>
      
            <Box sx={{ marginBottom: 2 }}>
                <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={handleCreate}
                >
                        {t("common.create")}
                </Button>
            </Box>
            <Box
                component="form"
                onSubmit={handleSearchSubmit}
                sx={{
                    display: "flex",
                    gap: 2,
                    marginBottom: 2,
                }}
            >
                <Grid container spacing={2}>
                    <Grid item xs={5} lg={2}>
                        <TextField
                            select
                            label={t("common.field")}
                            value={filterProperty}
                            onChange={(e) => setFilterProperty(e.target.value)}
                            SelectProps={{ native: true }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        >
                            <option value="">{t("common.default")}</option>
                            <option value="bo_user_name">{t("common.name")}</option>
                            <option value="bo_user_username">{t("common.username")}</option>
                            <option value="bo_deleted">{t("common.deleted")}</option>
                            <option value="bo_user_id">Id</option>
                        </TextField>
                    </Grid>
                    <Grid item xs={7} lg={7}>
                        {(() => {
                            switch (filterProperty) {
                                case "bo_deleted":
                                    return (
                                        <TextField
                                            select
                                            label={t("common.value")}
                                            variant="outlined"
                                            fullWidth
                                            onChange={(e) => setFilter(e.target.value)}
                                            value={filter}
                                            disabled={loading}
                                            SelectProps={{ native: true }}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            <option value="true">{t("common.yes")}</option>
                                            <option value="false">{t("common.no")}</option>
                                        </TextField>
                                    );
                                default:
                                    return (
                                        <TextField
                                            label={t("common.search")}
                                            variant="outlined"
                                            fullWidth
                                            type={filterProperty === "bo_user_id" ? "number" : "text"}
                                            onChange={(event) => setFilter(event.target.value)}
                                            value={filter}
                                            disabled={loading}
                                        />
                                    );
                            }
                        })()}
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={loading}
                        >
                            {t("common.search")}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                {query.filters?.map((f, index) => (
                    <Chip
                        key={index}
                        label={`${f.property === "Default" ? t("common.default") : f.property}: ${f.value}`}
                        onDelete={() => {
                            const newFilters = query.filters?.filter((_, i) => i !== index);
                            setQuery({ ...query, filters: newFilters, page: 0 });
                        }}
                        deleteIcon={<DeleteIcon />}
                    />
                ))}
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                {viewModel.map((item) => (
                    <Grid item
                        xs={12}
                        sm={viewModel.length === 1 ? 12 : viewModel.length === 2 ? 6 : 6}
                        md={viewModel.length === 1 ? 12 : viewModel.length === 2 ? 6 : 4}
                        key={item.user_id}>
                        <Card
                            onClick={() => handleSelect(item.user_id)}
                            sx={{
                                cursor: "pointer",
                                transition: "box-shadow 0.2s ease-in-out",
                                "&:hover": {
                                    boxShadow: 4,
                                    backgroundColor: "secondary.main",
                                },
                            }}
                        >
                            <CardContent>
                                <ListCardHeader
                                    icon={<GroupIcon fontSize="small" color="action" />}
                                    title={<span style={{ textDecoration: item.deleted || item.blocked ? "line-through" : "none" }}>{item.user_username}</span>}
                                    status={item.deleted
                                        ? {
                                            label: t("common.deleted"),
                                            color: "error",
                                            icon: <DeleteForeverIcon fontSize="small" />,
                                        }
                                        : item.blocked
                                            ? {
                                                label: t("common.blocked"),
                                                color: "warning",
                                                icon: <BlockIcon fontSize="small" />,
                                            }
                                            : undefined}
                                />
                                <Typography variant="body2" color="textSecondary" sx={{ textDecoration: item.deleted || item.blocked ? "line-through" : "none" }}>
                                    {t("common.name")}: {item.user_name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ textDecoration: item.deleted || item.blocked ? "line-through" : "none" }}>
                                    Id: {item.user_id}
                                </Typography>
            
                    
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
                {!loading && viewModel.length === 0 && (
                    <Grid item xs={12}>
                            <Typography align="center">{t("common.noEntriesFound")}</Typography>
                    </Grid>
                )}
            </Grid>

            {/* Pagination */}
            <ResponsivePagination

                page={query.page}

                pageSize={query.pageSize}

                totalCount={totalCount}

                onPageChange={handlePageChange}

            />

        </CenteredListCard>

    );
};

export default UserList;